import React from 'react';
import PullToRefresh from './PullToRefresh';
import SegmentedControl from './SegmentedControl';
import { ListEmpty, ListError, ListLoading } from './ListStates';

/**
 * The shared skeleton of every filtered list on a phone: title bar with an optional
 * action, one or more rows of filter pills, then a pull-to-refresh scroller that shows
 * loading, error, empty or rows.
 *
 * ## Why this exists
 *
 * Fleet bookings and fleet invoices had already written this twice, and the workshop's
 * bookings and invoices would have made four. The copies were not identical — one
 * forgot `onRetry`, the empty states named different things — which is exactly how a
 * fourth copy starts.
 *
 * ## What it does NOT own
 *
 * The rows. `renderItem` is a prop because a booking row and an invoice row have nothing
 * in common, and a component that tried to own both would grow a `type` flag and a
 * branch — the shape this extraction exists to avoid. The chrome is genuinely identical;
 * the contents genuinely are not, and only the first is shared.
 *
 * Data shaping is not here either: {@link useGroupedList} splits, sorts and counts. This
 * component takes the finished list and renders it. Keeping them apart means the sorting
 * rules can be tested without a DOM, and the chrome without a store.
 *
 * @param {string}   title
 * @param {React.ReactNode} [action]      right-hand header control, e.g. a create button
 * @param {Array}    [filters]            one entry per pill row:
 *                                        { ariaLabel, options, value, onChange }
 * @param {boolean}  loading
 * @param {string}   [error]
 * @param {Function} onRefresh            pull-to-refresh and error retry
 * @param {Array}    items                already filtered and sorted
 * @param {Function} renderItem           (item) => node; must supply its own key
 * @param {object}   empty                { icon, title, detail, action } for ListEmpty
 * @param {React.ReactNode} [banner]      optional strip under the filters
 * @param {React.ReactNode} [children]    modals and sheets, rendered outside the scroller
 */
const MobileListScreen = ({
    title,
    action,
    filters = [],
    loading,
    error,
    onRefresh,
    items,
    renderItem,
    empty,
    banner,
    children,
}) => (
    <>
        <header className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
            <h1 className="text-lg font-bold text-gray-900">{title}</h1>
            {action}
        </header>

        {filters.map((filter) => (
            <div key={filter.ariaLabel}
                 className="shrink-0 border-b border-gray-200 bg-white px-3 py-2">
                <SegmentedControl
                    ariaLabel={filter.ariaLabel}
                    options={filter.options}
                    value={filter.value}
                    onChange={filter.onChange}
                    disabled={loading}
                />
                {filter.detail}
            </div>
        ))}

        {banner}

        <PullToRefresh onRefresh={onRefresh} className="min-h-0 flex-1 overflow-y-auto">
            {loading && <ListLoading />}

            {!loading && error && <ListError message={error} onRetry={onRefresh} />}

            {!loading && !error && items.length === 0 && (
                <ListEmpty
                    icon={empty.icon}
                    title={empty.title}
                    detail={empty.detail}
                    action={empty.action}
                />
            )}

            {!loading && !error && items.length > 0 && (
                <ul>{items.map(renderItem)}</ul>
            )}
        </PullToRefresh>

        {children}
    </>
);

export default MobileListScreen;
